import React, { useEffect, useMemo, useRef, useState } from 'react';
import Graphic from '@arcgis/core/Graphic';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import PopupTemplate from '@arcgis/core/PopupTemplate';
import * as geometryJsonUtils from '@arcgis/core/geometry/support/jsonUtils';
import * as rendererJsonUtils from '@arcgis/core/renderers/support/jsonUtils';
import * as symbolJsonUtils from '@arcgis/core/symbols/support/jsonUtils';
import SimpleFillSymbol from '@arcgis/core/symbols/SimpleFillSymbol';
import SimpleLineSymbol from '@arcgis/core/symbols/SimpleLineSymbol';
import SimpleMarkerSymbol from '@arcgis/core/symbols/SimpleMarkerSymbol';
import { toast } from 'react-toastify';

import { Button } from '@/components/ui/button';
import { ReportsGeneratingPlaceholder } from 'components/Reports/ReportsGeneratingPlaceholder';
import { useReportAreaScope } from 'components/Reports/automated/useReportAreaScope';
import AssetExposureControls, {
  normalizeAssetExposureSetting,
  type AssetExposureSetting,
} from './AssetExposureControls';
import ChatLayerSelector from './ChatLayerSelector';
import {
  useRefreshSitrepV2,
  useUpdateSitrepSectionV2,
  type SitrepSectionType,
} from './hooks/useSitrepsV2';
import LayerStatsResultsView from './LayerStatsResultsView';
import {
  normalizeFloodWidgetFilters,
  normalizeLayerRankingSetting,
  parseLayerStatsPayload,
  type FloodWidgetFilters,
  type LayerRankingSetting,
} from './layerStatsFormat';
import type { SelectedLayerForAI } from './utils/prefetchLayerFeaturesForAI';
import {
  applyHideLayersOnMap,
  applyShowLayersOnMap,
  layersToShowOnMap,
  stampSpatialFilterOnLayers,
} from './utils/addSelectedLayerForAI';
import {
  geoSnapshotHasFeatures,
  parseGeoSnapshot,
  snapshotVersion,
  type GeoSnapshotLayer,
} from 'components/Reports/section/dynamicSectionSnapshot';
import { formatInBrowserTimeZone } from 'components/Reports/section/sectionLastUpdated';
import { useAnalyticsLayerActions } from 'components/Reports/document/AnalyticsLayerActions';
import {
  filterLayerStatsDataString,
  isVirtualAnalyticsLayerSection,
} from 'components/Reports/document/expandAnalyticsLayerSections';
import { useGroupGuid } from 'components/IAP/hooks/commonHooks';
import { useOptionalUscgMapContext } from 'components/MapPage/CorporateViewTabs/LayersTab/UscgMapContext';
import {
  SitStatExternalLayerItem,
  SitStatInternalLayerItem,
} from 'components/MapPage/CorporateViewTabs/SitStatTab/SitStatLayerItems';
import { useListExternalLayersQuery } from 'hooks/externalLayer/useListExternalLayersQuery';
import type { ExternalLayer } from 'hooks/externalLayer/types';
import { useListInternalLayersQuery } from 'hooks/internalLayer/useListInternalLayersQuery';
import type { InternalLayer } from 'hooks/internalLayer/types';
import {
  isAnalyticsSelectableLayer,
  sameExternalLayerForAi,
} from './utils/buildAiLayerCatalog';
import type { GetSitrepsResponse } from 'components/Reports/reports.types';
import type { SitrepSpatialScope } from 'components/Reports/utils/resolveSitrepSpatialFilter';

type LayersDataSource = {
  source: string;
  selectedLayers?: SelectedLayerForAI[];
  filterByDatetime?: boolean;
  floodFilters?: FloodWidgetFilters;
  spatialFilter?: SelectedLayerForAI['spatialFilter'];
  spatialFilterLabel?: string;
  assetExposure?: Partial<AssetExposureSetting>;
  ranking?: Partial<LayerRankingSetting>;
};

type SitrepSectionLike = {
  id: string;
  type?: string;
  data_sources?: LayersDataSource[];
  automation?: { provider?: string; [key: string]: unknown };
  output_format_structured?: {
    automation?: { provider?: string; [key: string]: unknown };
    summary_options?: string[];
    [key: string]: unknown;
  };
  frozen_version_id?: string | null;
  section_versions?: Array<{
    version_id?: string | null;
    id?: string | null;
    data_string?: string | null;
    status?: string | null;
    timestamp?: string | null;
  }>;
};

export function isAutomatedLayerStatsSection(
  section: SitrepSectionLike | null | undefined
): boolean {
  return (
    section?.type === 'automated' &&
    (section.automation?.provider === 'layerStats' ||
      section.output_format_structured?.automation?.provider === 'layerStats')
  );
}

function getLayersEntry(
  section: SitrepSectionLike | null | undefined
): LayersDataSource | null {
  const sources = Array.isArray(section?.data_sources)
    ? section.data_sources
    : [];
  return sources.find((ds) => ds?.source === 'Layers') || null;
}

function initialFloodFilters(
  entry: LayersDataSource | null
): FloodWidgetFilters {
  if (entry?.floodFilters) {
    return normalizeFloodWidgetFilters(entry.floodFilters);
  }
  // Prefer TN for FloodingBins demo until the operator chooses otherwise.
  return normalizeFloodWidgetFilters({
    stateAbbr: 'TN',
    confidenceOverallMin: null,
  });
}

function layersMatchScope(
  layers: SelectedLayerForAI[],
  scope: Pick<SitrepSpatialScope, 'spatialFilter' | 'spatialFilterLabel'> | null
): boolean {
  if (!scope) {
    return layers.every((layer) => !layer.spatialFilter);
  }
  return layers.every(
    (layer) =>
      layer.spatialFilterLabel === scope.spatialFilterLabel &&
      JSON.stringify(layer.spatialFilter) ===
        JSON.stringify(scope.spatialFilter)
  );
}

type AutomatedLayerStatsSectionPanelProps = {
  sitrepId: string;
  section: SitrepSectionLike;
  sitrep?: Pick<
    GetSitrepsResponse,
    'aor_id' | 'incident_id' | 'approval_status'
  > &
    Partial<
      Pick<
        GetSitrepsResponse,
        | 'id'
        | 'spatial_filter'
        | 'spatial_filter_label'
        | 'scope_source'
        | 'approved_at'
        | 'approvals'
      >
    >;
  selectedDatetime?: string;
  disabled?: boolean;
  dataStringOverride?: string | null;
  hideRun?: boolean;
  /** Current report, Live Data: rerun the analytics query. */
  onRefreshAnalytics?: () => void;
  refreshingAnalytics?: boolean;
};

function mapVisibilityApi(
  mapCtx: NonNullable<ReturnType<typeof useOptionalUscgMapContext>>
) {
  return {
    isExternalLayerVisible: mapCtx.isExternalLayerVisible,
    toggleExternalLayerVisibility: mapCtx.toggleExternalLayerVisibility,
    isInternalLayerVisible: mapCtx.isInternalLayerVisible,
    toggleInternalLayerVisibility: mapCtx.toggleInternalLayerVisibility,
  };
}

const GREY = [140, 140, 140, 0.9] as const;
const GREY_FILL = [140, 140, 140, 0.35] as const;
const GREY_LINE = [110, 110, 110, 0.95] as const;

function symbolForGeometry(geometry: __esri.GeometryUnion) {
  if (geometry.type === 'polygon' || geometry.type === 'extent') {
    return new SimpleFillSymbol({
      color: [...GREY_FILL],
      outline: { color: [...GREY_LINE], width: 1.5 },
    });
  }
  if (geometry.type === 'polyline') {
    return new SimpleLineSymbol({ color: [...GREY_LINE], width: 2 });
  }
  return new SimpleMarkerSymbol({
    color: [...GREY],
    size: 8,
    outline: { color: [255, 255, 255, 0.9], width: 1 },
  });
}

function isColorArray(value: unknown): value is number[] {
  return (
    Array.isArray(value) &&
    value.length >= 3 &&
    value.length <= 4 &&
    value.every((part) => typeof part === 'number')
  );
}

/** Keep icon shape and size; replace every stored color with grey. */
function recolorSymbolJson(node: unknown): void {
  if (!node || typeof node !== 'object') return;
  if (Array.isArray(node)) {
    for (const item of node) recolorSymbolJson(item);
    return;
  }
  const record = node as Record<string, unknown>;
  for (const [key, value] of Object.entries(record)) {
    if (/color$/i.test(key) && isColorArray(value)) {
      const alpha = value.length > 3 ? value[3] : 255;
      record[key] = [140, 140, 140, alpha];
      continue;
    }
    recolorSymbolJson(value);
  }
}

function greySymbol(symbol: __esri.Symbol): __esri.SymbolUnion {
  try {
    const json = symbol.toJSON() as Record<string, unknown>;
    recolorSymbolJson(json);
    return (symbolJsonUtils.fromJSON(json as __esri.SymbolProperties) ??
      symbol) as __esri.SymbolUnion;
  } catch {
    return symbol as __esri.SymbolUnion;
  }
}

async function symbolForSnapshotFeature(
  rendererJson: Record<string, unknown> | undefined,
  geometry: __esri.GeometryUnion,
  attributes: Record<string, unknown>
): Promise<__esri.SymbolUnion> {
  const fallback = symbolForGeometry(geometry);
  if (!rendererJson) return fallback;
  try {
    const renderer = rendererJsonUtils.fromJSON(
      rendererJson as __esri.RendererProperties
    );
    if (!renderer) return fallback;
    const probe = new Graphic({ geometry, attributes });
    let resolved: __esri.Symbol | null | undefined = null;
    if ('getSymbol' in renderer && typeof renderer.getSymbol === 'function') {
      resolved = renderer.getSymbol(probe);
    }
    if (
      !resolved &&
      'getSymbolAsync' in renderer &&
      typeof renderer.getSymbolAsync === 'function'
    ) {
      resolved = await renderer.getSymbolAsync(probe);
    }
    return resolved ? greySymbol(resolved) : fallback;
  } catch {
    return fallback;
  }
}

const HIDDEN_POPUP_FIELDS = /^(OBJECTID|FID|snapshotLayerName|Shape__)/i;

function fieldLabel(name: string): string {
  return name
    .replace(/_/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim();
}

function featureTitle(
  attributes: Record<string, unknown> | undefined,
  layerName: string
): string {
  for (const key of ['IncidentName', 'STORMNAME', 'name', 'Name']) {
    const value = attributes?.[key];
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return layerName;
}

function popupForSnapshotFeature(
  attributes: Record<string, unknown>,
  layerName: string,
  capturedLabel: string | null
): PopupTemplate {
  const fieldInfos = Object.entries(attributes)
    .filter(
      ([key, value]) =>
        !HIDDEN_POPUP_FIELDS.test(key) && value != null && value !== ''
    )
    .map(([fieldName]) => ({ fieldName, label: fieldLabel(fieldName) }));
  const annotation = capturedLabel
    ? `Historical Snapshot from ${capturedLabel}`
    : 'Historical Snapshot';
  return new PopupTemplate({
    title: featureTitle(attributes, layerName),
    content: [
      { type: 'text', text: annotation },
      ...(fieldInfos.length > 0 ? [{ type: 'fields', fieldInfos }] : []),
    ],
  });
}

function useShowSavedLayersOnMap(
  enabled: boolean,
  layers: SelectedLayerForAI[]
) {
  const mapCtx = useOptionalUscgMapContext();
  const shownRef = useRef(false);

  useEffect(() => {
    if (!enabled) {
      shownRef.current = false;
      return;
    }
    if (shownRef.current || !mapCtx) return;
    const items = layersToShowOnMap([], layers);
    if (items.length === 0) return;
    applyShowLayersOnMap(items, mapVisibilityApi(mapCtx));
    mapCtx.setIsMapVisible(true);
    shownRef.current = true;
  }, [enabled, layers, mapCtx]);
}

function showLiveAnalyticsLayers(
  mapCtx: NonNullable<ReturnType<typeof useOptionalUscgMapContext>>,
  liveLayers: SelectedLayerForAI[]
) {
  const items = layersToShowOnMap([], liveLayers);
  if (items.length === 0) return;
  applyShowLayersOnMap(items, mapVisibilityApi(mapCtx));
  mapCtx.setIsMapVisible(true);
}

function useAnalyticsGeoSnapshot(
  enabled: boolean,
  geoLayers: GeoSnapshotLayer[],
  liveLayers: SelectedLayerForAI[],
  capturedLabel: string | null
) {
  const mapCtx = useOptionalUscgMapContext();
  const map = mapCtx?.mapView?.map ?? null;
  const wasEnabledRef = useRef(enabled);
  const enabledRef = useRef(enabled);
  const mapCtxRef = useRef(mapCtx);
  const geoLayersRef = useRef(geoLayers);
  const liveLayersRef = useRef(liveLayers);
  const capturedLabelRef = useRef(capturedLabel);
  enabledRef.current = enabled;
  mapCtxRef.current = mapCtx;
  geoLayersRef.current = geoLayers;
  liveLayersRef.current = liveLayers;
  capturedLabelRef.current = capturedLabel;
  const geoKey = geoLayers
    .map((layer) => `${layer.id}:${layer.features.length}`)
    .join('|');
  const liveKey = liveLayers
    .map((layer) => `${layer.kind}:${layer.id}`)
    .join('|');

  useEffect(() => {
    const ctx = mapCtxRef.current;
    const currentMap = ctx?.mapView?.map;
    const currentGeo = geoLayersRef.current;
    const currentLive = liveLayersRef.current;
    const currentLabel = capturedLabelRef.current;
    if (!enabled || !currentMap || !ctx) return;

    const items = layersToShowOnMap([], currentLive);
    if (items.length > 0) {
      applyHideLayersOnMap(items, mapVisibilityApi(ctx));
    }

    const graphicsLayer = new GraphicsLayer({
      title: 'Analytics snapshot',
      listMode: 'hide',
    });
    let cancelled = false;
    void (async () => {
      for (const layer of currentGeo) {
        for (const feature of layer.features) {
          if (cancelled || !feature.geometry) continue;
          const geometry = geometryJsonUtils.fromJSON(
            feature.geometry as __esri.GeometryProperties
          );
          if (!geometry) continue;
          const attributes = {
            ...(feature.attributes ?? {}),
            snapshotLayerName: layer.name,
          };
          const symbol = await symbolForSnapshotFeature(
            layer.renderer,
            geometry,
            attributes
          );
          if (cancelled) return;
          graphicsLayer.add(
            new Graphic({
              geometry,
              attributes,
              symbol,
              popupTemplate: popupForSnapshotFeature(
                attributes,
                layer.name,
                currentLabel
              ),
            })
          );
        }
      }
      if (cancelled || graphicsLayer.graphics.length === 0) return;
      currentMap.add(graphicsLayer);
      mapCtxRef.current?.setIsMapVisible(true);
    })();
    return () => {
      cancelled = true;
      if (
        graphicsLayer.graphics.length > 0 &&
        currentMap.layers.includes(graphicsLayer)
      ) {
        currentMap.remove(graphicsLayer);
      }
    };
  }, [capturedLabel, enabled, geoKey, liveKey, map]);

  useEffect(() => {
    const wasEnabled = wasEnabledRef.current;
    wasEnabledRef.current = enabled;
    const ctx = mapCtxRef.current;
    if (wasEnabled && !enabled && ctx) {
      showLiveAnalyticsLayers(ctx, liveLayersRef.current);
    }
  }, [enabled]);

  useEffect(() => {
    return () => {
      const ctx = mapCtxRef.current;
      if (enabledRef.current && ctx) {
        showLiveAnalyticsLayers(ctx, liveLayersRef.current);
      }
    };
  }, []);
}

function matchesSavedExternalLayer(
  saved: SelectedLayerForAI,
  layer: ExternalLayer
) {
  return (
    layer.arcgisItemId === saved.id ||
    sameExternalLayerForAi(saved, {
      kind: 'external',
      id: String(layer.id),
      serviceUrl: layer.serviceUrl,
    })
  );
}

type SavedLayerRow =
  | { kind: 'internal'; layer: InternalLayer }
  | { kind: 'external'; layer: ExternalLayer };

function SavedAnalyticsLayerRows({ layers }: { layers: SelectedLayerForAI[] }) {
  const mapCtx = useOptionalUscgMapContext();
  const groupGuid = useGroupGuid();
  const listEnabled = Boolean(groupGuid) && Boolean(mapCtx);
  const externalQuery = useListExternalLayersQuery(
    {
      filter: { groupGuid: groupGuid ?? '' },
      perPage: 200,
    },
    { enabled: listEnabled }
  );
  const internalQuery = useListInternalLayersQuery({
    filter: { groupGuid: groupGuid ?? '' },
    perPage: 200,
  });

  if (!mapCtx || layers.length === 0) return null;

  const externalLayers = externalQuery.data?.data ?? [];
  const internalLayers = internalQuery.data?.data ?? [];
  const rows: SavedLayerRow[] = [];
  for (const saved of layers) {
    if (saved.kind === 'internal') {
      const layer = internalLayers.find((item) => String(item.id) === saved.id);
      if (layer) rows.push({ kind: 'internal', layer });
      continue;
    }
    const layer = externalLayers.find((item) =>
      matchesSavedExternalLayer(saved, item)
    );
    if (layer) rows.push({ kind: 'external', layer });
  }

  if (rows.length === 0) return null;

  return (
    <div className="dark tw:space-y-2 tw:text-foreground">
      {rows.map((row) =>
        row.kind === 'internal' ? (
          <SitStatInternalLayerItem
            key={`internal-${row.layer.id}`}
            layer={row.layer}
            onZoomToLayer={() => {
              void mapCtx.focusLayerOnMap({
                kind: 'internal',
                layerId: row.layer.id,
                name: row.layer.name,
                lastUpdated: row.layer.updatedAt,
              });
            }}
          />
        ) : (
          <SitStatExternalLayerItem
            key={`external-${row.layer.id}`}
            layer={row.layer}
            onZoomToLayer={() => {
              void mapCtx.focusLayerOnMap({
                kind: 'external',
                layerId: row.layer.id,
                name: row.layer.name,
                lastUpdated: row.layer.updatedAt,
              });
            }}
            onZoomToGraphic={(graphic) => {
              void mapCtx.focusFeatureOnMap({ graphic });
            }}
          />
        )
      )}
    </div>
  );
}

/**
 * Layers + datetime config and operator-focused LayerQueryResults widgets for
 * automated Analytics sections.
 */
export default function AutomatedLayerStatsSectionPanel({
  sitrepId,
  section,
  sitrep,
  selectedDatetime = undefined,
  disabled = false,
  dataStringOverride,
  hideRun = false,
  onRefreshAnalytics,
  refreshingAnalytics = false,
}: AutomatedLayerStatsSectionPanelProps) {
  const layersEntry = getLayersEntry(section);
  const { effectiveScope } = useReportAreaScope({
    sitrep,
    sitrepId,
    persist: false,
  });
  const [selectedLayers, setSelectedLayers] = useState<SelectedLayerForAI[]>(
    () => layersEntry?.selectedLayers || []
  );
  const [filterByDatetime, setFilterByDatetime] = useState(() => {
    if (layersEntry?.filterByDatetime === true) return true;
    if (layersEntry?.filterByDatetime === false) return false;
    return (layersEntry?.selectedLayers || []).some(
      (l) => l?.timeExtent || l?.timeFilterLabel
    );
  });
  const [floodFilters, setFloodFilters] = useState<FloodWidgetFilters>(() =>
    initialFloodFilters(layersEntry)
  );
  const [assetExposure, setAssetExposure] = useState<AssetExposureSetting>(() =>
    normalizeAssetExposureSetting(layersEntry?.assetExposure)
  );
  const [ranking, setRanking] = useState<LayerRankingSetting>(() =>
    normalizeLayerRankingSetting(layersEntry?.ranking)
  );

  const updateSitrepSectionMutation = useUpdateSitrepSectionV2();
  const refreshSitrepMutation = useRefreshSitrepV2();
  const layerActions = useAnalyticsLayerActions();
  const isVirtualLayer = isVirtualAnalyticsLayerSection(section);
  const isLayerChild =
    Boolean(
      (section as { parent_section_id?: string | null }).parent_section_id
    ) || isVirtualLayer;
  const manageAsCategory = Boolean(layerActions) && !isLayerChild;

  const version = section?.section_versions?.[0] || null;
  const displayedDataString = filterLayerStatsDataString(
    dataStringOverride !== undefined
      ? dataStringOverride
      : version?.data_string,
    selectedLayers
  );
  const payload = useMemo(
    () => parseLayerStatsPayload(displayedDataString),
    [displayedDataString]
  );
  const geoLayers = useMemo(
    () => parseGeoSnapshot(displayedDataString),
    [displayedDataString]
  );
  const showGeoSnapshot = geoSnapshotHasFeatures(geoLayers);
  const isApprovedReport = sitrep?.approval_status === 'APPROVED';
  const capturedAt = isApprovedReport
    ? sitrep?.approved_at || sitrep?.approvals?.[0]?.approved_at
    : snapshotVersion(section)?.timestamp;
  const capturedLabel = capturedAt ? formatInBrowserTimeZone(capturedAt) : null;
  const showSavedLayers = hideRun && isApprovedReport && !showGeoSnapshot;
  useShowSavedLayersOnMap(showSavedLayers, selectedLayers);
  useAnalyticsGeoSnapshot(
    showGeoSnapshot,
    geoLayers,
    selectedLayers,
    capturedLabel
  );

  const isProcessing = useMemo(() => {
    if (!version) return false;
    const status = (version.status || '').toLowerCase();
    return (
      !!status &&
      status !== 'completed' &&
      status !== 'summarized' &&
      status !== 'error'
    );
  }, [version]);

  useEffect(() => {
    const entry = getLayersEntry(section);
    setSelectedLayers(entry?.selectedLayers || []);
    if (entry?.filterByDatetime === true) setFilterByDatetime(true);
    else if (entry?.filterByDatetime === false) setFilterByDatetime(false);
    else {
      setFilterByDatetime(
        (entry?.selectedLayers || []).some(
          (l) => l?.timeExtent || l?.timeFilterLabel
        )
      );
    }
    setFloodFilters(initialFloodFilters(entry));
    setAssetExposure(normalizeAssetExposureSetting(entry?.assetExposure));
    setRanking(normalizeLayerRankingSetting(entry?.ranking));
  }, [section?.id, section?.data_sources]);

  useEffect(() => {
    setSelectedLayers((prev) => {
      if (prev.length === 0 || layersMatchScope(prev, effectiveScope)) {
        return prev;
      }
      if (!effectiveScope) {
        return prev.map((layer) => {
          const {
            spatialFilter: _sf,
            spatialFilterLabel: _sfl,
            ...rest
          } = layer;
          return rest;
        });
      }
      return stampSpatialFilterOnLayers(prev, effectiveScope);
    });
  }, [effectiveScope, selectedLayers.length]);

  const isBusy =
    disabled ||
    isProcessing ||
    updateSitrepSectionMutation.isPending ||
    refreshSitrepMutation.isPending;

  const buildDataSources = (
    nextFloodFilters: FloodWidgetFilters = floodFilters,
    layers: SelectedLayerForAI[] = selectedLayers,
    nextExposure: AssetExposureSetting = assetExposure,
    nextRanking: LayerRankingSetting = ranking
  ) => [
    {
      source: 'Layers',
      selectedLayers: layers,
      filterByDatetime,
      floodFilters: normalizeFloodWidgetFilters(nextFloodFilters),
      assetExposure: nextExposure,
      ranking: nextRanking,
      // The same polygon is already stamped on each selected layer. Repeating
      // it here pushed the section save over the 1MB JSON limit.
      ...(effectiveScope
        ? {
            spatialFilterLabel: effectiveScope.spatialFilterLabel,
          }
        : {}),
    },
  ];

  const runAnalytics = (
    nextFloodFilters: FloodWidgetFilters = floodFilters,
    nextRanking: LayerRankingSetting = ranking
  ) => {
    const automation = {
      ...(section.automation ||
        section.output_format_structured?.automation ||
        {}),
      provider: 'layerStats' as const,
    };
    const summary_options =
      section.output_format_structured?.summary_options || [];
    const scopedLayers = effectiveScope
      ? stampSpatialFilterOnLayers(selectedLayers, effectiveScope)
      : selectedLayers;
    const dataSources = buildDataSources(
      nextFloodFilters,
      scopedLayers,
      assetExposure,
      nextRanking
    );

    const updatedFields = {
      type: 'automated' as const,
      data_sources: dataSources,
      automation,
      output_format_structured: {
        ...section.output_format_structured,
        summary_options,
        automation,
      },
    };

    updateSitrepSectionMutation.mutate(
      {
        sitrepId,
        sectionId: section.id,
        updatedFields,
      },
      {
        onSuccess: () => {
          refreshSitrepMutation.mutate(
            {
              id: sitrepId,
              selectedDatetime:
                selectedDatetime ||
                version?.timestamp ||
                new Date().toISOString(),
              sections: [section.id],
              // Bypass stale React Query cache — pass the just-saved selection.
              layerSelectionsBySectionId: {
                [section.id]: dataSources[0]?.selectedLayers || [],
              },
            },
            {
              onError: (error) => {
                toast.error('Failed to refresh Analytics section.');
                console.error('Error refreshing Analytics section:', error);
              },
            }
          );
        },
        onError: (error) => {
          toast.error('Failed to save Layers selection.');
          console.error('Error saving Analytics Layers:', error);
        },
      }
    );
  };

  const handleRun = () => {
    runAnalytics(floodFilters);
  };

  const handleAssetExposureChange = (next: AssetExposureSetting) => {
    setAssetExposure(next);
    if (!manageAsCategory) return;
    // Category parents have no Run button; save so new layer sections inherit it.
    updateSitrepSectionMutation.mutate(
      {
        sitrepId,
        sectionId: section.id,
        silent: true,
        updatedFields: {
          data_sources: buildDataSources(floodFilters, selectedLayers, next),
        },
      },
      {
        onError: (error) => {
          toast.error('Failed to save asset exposure setting.');
          console.error('Error saving asset exposure:', error);
        },
      }
    );
  };

  const handleFloodFiltersChange = (next: FloodWidgetFilters) => {
    const normalized = normalizeFloodWidgetFilters(next);
    setFloodFilters(normalized);
    if (selectedLayers.length === 0) return;
    runAnalytics(normalized);
  };

  const handleRankingChange = (next: LayerRankingSetting) => {
    setRanking(next);
    if (selectedLayers.length === 0) return;
    runAnalytics(floodFilters, next);
  };

  const rankingEditable = !hideRun && !isVirtualLayer && !isApprovedReport;

  return (
    <div className="AutomatedLayerStatsSectionPanel dark tw:rounded-lg tw:border tw:border-border tw:bg-background tw:p-3 tw:text-foreground">
      <div className="mb-3">
        <div className="tw:mb-2 tw:flex tw:flex-wrap tw:items-center tw:justify-start tw:gap-2">
          <strong>Layer analytics</strong>
          {onRefreshAnalytics ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={isBusy || refreshingAnalytics}
              onClick={onRefreshAnalytics}
            >
              Refresh Analytics
            </Button>
          ) : null}
        </div>
        {showSavedLayers ? (
          <SavedAnalyticsLayerRows layers={selectedLayers} />
        ) : null}
        {hideRun && !isLayerChild ? (
          <ChatLayerSelector
            selectedLayers={selectedLayers}
            onChange={setSelectedLayers}
            syncToMap
            syncOnly
            defaultSpatialFilter={effectiveScope?.spatialFilter}
            defaultSpatialFilterLabel={effectiveScope?.spatialFilterLabel}
            isLayerAllowed={isAnalyticsSelectableLayer}
          />
        ) : null}
        {hideRun || isLayerChild ? null : (
          <div className="tw:flex tw:flex-col tw:gap-2">
            <ChatLayerSelector
              selectedLayers={selectedLayers}
              onChange={(next) => {
                if (manageAsCategory && layerActions) {
                  void layerActions.commitLayers(
                    section as SitrepSectionType,
                    next
                  );
                  return;
                }
                setSelectedLayers(next);
              }}
              filterByDatetime={filterByDatetime}
              onFilterByDatetimeChange={setFilterByDatetime}
              syncToMap
              defaultSpatialFilter={effectiveScope?.spatialFilter}
              defaultSpatialFilterLabel={effectiveScope?.spatialFilterLabel}
              isLayerAllowed={isAnalyticsSelectableLayer}
            />
            {manageAsCategory ? null : (
              <Button
                type="button"
                disabled={isBusy || selectedLayers.length === 0}
                onClick={handleRun}
              >
                Run Analytics
              </Button>
            )}
          </div>
        )}
        {hideRun || isVirtualLayer ? null : (
          <div className="tw:mt-3">
            <AssetExposureControls
              value={assetExposure}
              disabled={isBusy}
              onChange={handleAssetExposureChange}
            />
          </div>
        )}
        {hideRun || !isLayerChild || isVirtualLayer ? null : (
          <Button
            type="button"
            disabled={isBusy || selectedLayers.length === 0}
            onClick={handleRun}
          >
            Run Analytics
          </Button>
        )}
      </div>
      {isProcessing || refreshingAnalytics ? (
        <ReportsGeneratingPlaceholder message="PRATUS is analyzing data..." />
      ) : payload ? (
        <LayerStatsResultsView
          payload={payload}
          floodFilters={floodFilters}
          onFloodFiltersChange={handleFloodFiltersChange}
          filtersDisabled={isBusy}
          onRankingChange={rankingEditable ? handleRankingChange : undefined}
        />
      ) : (
        <p className="tw:mb-0 tw:text-muted-foreground">
          Configure Layers above and click Run Analytics to fetch stats.
        </p>
      )}
    </div>
  );
}
